import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

export default function Terms() {
  const page = webPageSchema({
    path: "/terms",
    title: "Terms of Use for shariqrefai.com | Shariq Refai, MD",
    description:
      "Terms of use for shariqrefai.com. Educational and informational use only, no medical advice or physician-patient relationship is created by use of this site.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Terms of Use", path: "/terms" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Terms of Use for shariqrefai.com | Shariq Refai, MD"
        description="Terms of use for shariqrefai.com. Educational and informational use only, no medical advice or physician-patient relationship is created by use of this site."
        url="/terms"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Terms", path: "/terms" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Terms of Use</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Terms of Use
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            The ground rules for using this website.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: September 23, 2026.</strong>
          </p>

          <p>
            These Terms of Use ("Terms") govern your access to and use of
            shariqrefai.com (the "website"). By using the website, you agree
            to these Terms. If you do not agree, please do not use the
            website.
          </p>

          <h2>1. Educational and Informational Use Only</h2>
          <p>
            The content on this website is provided for general educational
            and informational purposes only and is not medical advice or a
            substitute for individualized psychiatric or medical care.
          </p>
          <p>
            The website is an author, publishing, and media platform. All
            content is provided for general educational and informational
            purposes only. Nothing on this website constitutes medical
            advice, psychiatric treatment, therapy, diagnosis, or
            individualized clinical guidance. See the full{" "}
            <Link href="/disclaimer">Medical &amp; Educational Disclaimer</Link>.
          </p>

          <h2>2. No Physician-Patient Relationship</h2>
          <p>
            Viewing this website, reading its content, or submitting
            information through the website does not establish a
            physician-patient relationship.
          </p>
          <p>
            Your use of this website does not, and is not intended to,
            create any physician-patient, therapist-client, treatment,
            fiduciary, or confidential medical relationship between you and
            Shariq Refai, MD, MBA, or any affiliated organization, including
            shrinkMD or Unstuck. This includes, without limitation:
          </p>
          <ul>
            <li>Reading, browsing, or accessing the website or any of its pages.</li>
            <li>Reading any book, article, essay, framework, or downloadable material authored by or attributed to Shariq Refai.</li>
            <li>Submitting an inquiry through the contact form or any other form on the website.</li>
            <li>Sending an email or other communication referencing the website.</li>
            <li>Following, subscribing to, or interacting with Shariq Refai on any social, professional, or media platform.</li>
            <li>Listening to or viewing any podcast, interview, video, or other media featuring Shariq Refai.</li>
            <li>Attending, viewing, or participating in any keynote, lecture, panel, workshop, podcast, livestream, or other educational presentation given by Shariq Refai.</li>
          </ul>
          <p>
            Nothing on this website is, or should be interpreted as,
            individualized medical, psychiatric, or mental health
            recommendations for you or any other person.
          </p>

          <h2>3. No Crisis Use</h2>
          <p>
            The website is not designed or intended to be used in a medical or
            mental health emergency. If you are in crisis or feel unsafe, call
            or text <a href="tel:988">988</a> in the United States, call{" "}
            <a href="tel:911">911</a>, or go to the nearest emergency room.
          </p>

          <h2>4. Intellectual Property</h2>
          <p>
            All content on the website, including text, graphics, logos, book
            excerpts, images, and design elements, is the property of Shariq
            Refai, MD, MBA, or its respective rights holders, and is
            protected by United States and international copyright,
            trademark, and other intellectual property laws. You may view and
            share this content for personal, non-commercial use with
            attribution. Republishing, reselling, or commercial reuse without
            written permission is not allowed.
          </p>
          <p>
            Search engines, AI answer engines, and AI and LLM crawlers may
            read, index, cite, retrieve, and train on the public pages of the
            website, as long as attribution to Shariq Refai, MD, MBA is kept
            and the content isn't presented as medical advice. The full
            terms are on the <Link href="/copyright">Copyright</Link> page and
            in <a href="/ai.txt">ai.txt</a>.
          </p>

          <h2>5. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the website in any way that violates any applicable law or regulation.</li>
            <li>Attempt to gain unauthorized access to any portion of the website or its underlying systems.</li>
            <li>Interfere with or disrupt the website, its servers, or any networks connected to it.</li>
            <li>Use the website to transmit any harmful or unlawful content, or to harass any individual.</li>
            <li>Scrape, copy, or systematically extract content from the website in a way that ignores its robots.txt directives, degrades the site for readers, or goes beyond the AI and crawler permissions described in Section 4.</li>
          </ul>

          <h2>6. Third-Party Sites and Services</h2>
          <p>
            The website may contain links to third-party websites and services,
            including{" "}
            <a href={withNetworkUtm("https://shrinkmd.com/", "terms_page")} target="_blank" rel="noopener noreferrer">
              shrinkMD
            </a>{" "}
            and{" "}
            <a href={withNetworkUtm("https://beunstuck.app/", "terms_page")} target="_blank" rel="noopener noreferrer">
              Unstuck
            </a>
            . Those services are governed by their own terms and privacy
            policies, and you use them at your own discretion. The website is
            not responsible for the content, policies, or practices of any
            third party.
          </p>

          <h2>7. Disclaimer of Warranties</h2>
          <p>
            The website is provided on an "as is" and "as available" basis,
            without warranties of any kind, express or implied, including but
            not limited to warranties of merchantability, fitness for a
            particular purpose, non-infringement, or accuracy. The website
            does not warrant that the content is current, accurate, complete,
            or error-free.
          </p>

          <h2>8. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, and except for
            liability that cannot be limited or excluded by law (including
            liability for fraud, gross negligence, willful misconduct, or
            personal injury caused by such conduct), Shariq Refai, MD, MBA,
            and any affiliated entities will not be liable for any indirect,
            incidental, consequential, special, exemplary, or punitive
            damages, or any loss of profits, revenue, data, business, or
            goodwill, arising out of or in connection with your use of the
            website, even if advised of the possibility of such damages.
          </p>
          <p>
            In all events, the aggregate liability of Shariq Refai, MD, MBA,
            and any affiliated entities for any claim arising out of or
            related to the website or these Terms shall not exceed one
            hundred U.S. dollars (US$100.00), which the parties agree is a
            reasonable allocation of risk for a free, educational website.
          </p>

          <h2>9. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless Shariq Refai,
            MD, MBA, and his licensors, contractors, agents, and affiliated
            entities (the "Indemnified Parties") from and against any
            third-party claim, liability, damage, loss, cost, or expense
            (including reasonable attorneys' fees) arising out of or related
            to (a) your use of the website; (b) your violation of these
            Terms; (c) your violation of any third-party right, including
            any intellectual property or privacy right; or (d) any content
            you submit through the website.
          </p>
          <p>
            The Indemnified Parties will provide prompt written notice of
            any claim subject to indemnification and reasonable cooperation
            in the defense. The Indemnified Parties reserve the right to
            assume the exclusive defense and control of any matter subject
            to indemnification, in which case you agree to cooperate at your
            expense.
          </p>

          <h2>10. Governing Law; Venue; Class Action Waiver; Jury Trial Waiver; Limitations Period</h2>
          <p>
            These Terms are governed by the laws of the State of Florida,
            United States, without regard to its conflict-of-laws principles.
            Any claim or dispute arising out of these Terms or your use of
            the website shall be brought exclusively in the state or federal
            courts located in Duval County, Florida, and you consent to
            personal jurisdiction in those courts.
          </p>
          <p>
            You and Shariq Refai, MD, MBA each waive any right to a jury
            trial in any action arising out of or related to these Terms or
            your use of the website, to the fullest extent permitted by law.
          </p>
          <p>
            You agree that any claim must be brought in your individual
            capacity and not as a plaintiff or class member in any purported
            class, collective, or representative proceeding. The class-action
            waiver does not apply to claims that cannot be waived under
            applicable law.
          </p>
          <p>
            Any claim arising out of or related to these Terms or your use
            of the website must be filed within one (1) year after the claim
            accrued, or it is permanently barred, unless applicable law
            requires a longer period.
          </p>

          <h2>11. Changes to These Terms</h2>
          <p>
            These Terms may be updated from time to time. The updated version
            will be posted on this page with a revised "Last updated" date.
            For material changes, reasonable advance notice will be provided
            on the homepage or in a notice on this page for at least thirty
            (30) days before the change becomes effective. Material changes
            will not be applied retroactively. Continued use of the website
            after non-material changes are posted constitutes acceptance of
            the updated Terms.
          </p>

          <h2>12. Equitable Relief</h2>
          <p>
            Notwithstanding any other provision, Shariq Refai, MD, MBA may
            seek injunctive or other equitable relief in any court of
            competent jurisdiction to protect intellectual property or to
            enforce confidentiality and other obligations in these Terms.
          </p>

          <h2>13. No Practice of Medicine; Multistate Licensure</h2>
          <p>
            Dr. Refai holds active medical licensure in certain U.S.
            jurisdictions through his clinical practice at shrinkMD. No
            content on this website is offered under any state medical
            license, and no element of this website should be construed as
            the practice of medicine in any U.S. or foreign jurisdiction. The
            fact that Dr. Refai holds medical licensure in one or more
            jurisdictions does not create any clinical relationship through
            this website or extend any license to readers in any other
            jurisdiction.
          </p>

          <h2>14. International Users</h2>
          <p>
            This website is operated from the United States and intended
            primarily for U.S. audiences. If you access the website from
            outside the United States, you do so on your own initiative and
            are responsible for compliance with local law. Educational
            content reflects U.S. clinical practice frameworks and is not
            adapted to any non-U.S. healthcare system. Outside the United
            States, contact your local emergency services in any crisis.
          </p>

          <h2>15. Linked Resources</h2>
          <p>
            Any third-party site or resource linked from this website,
            including but not limited to shrinkmd.com, beunstuck.app,
            anxietyresource.org, depressionresource.org,
            anxietyresearch.org, and psychiatryrx.org, is provided for
            reference only. Linked resources are independently operated and
            have their own terms, privacy policies, and editorial standards.
            No link constitutes endorsement, sponsorship, or warranty of any
            kind.
          </p>

          <h2>16. Contact</h2>
          <p>
            For questions about these Terms, email{" "}
            <a href="mailto:hello@shariqrefai.com">hello@shariqrefai.com</a>{" "}
            or use the <Link href="/contact">contact page</Link>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
